import { Quad } from 'gsap';
import type { RefObject } from 'react';
import { useCallback, useContext, useRef } from 'react';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import { enterFullScreenTransition, exitFullScreenTransition } from '../HeaderShowreel.transitions';
import useBackgroundFan from './useBackgroundFan';
import useScrollTrigger from '../../../../hooks/useScrollTrigger';
import { useScrollProviderContext } from '../../../unlisted/ScrollProvider';
import { SiteHeaderContext } from '../../../unlisted/SiteHeaderContext';
import type { SiteHeaderContextType } from '../../../unlisted/SiteHeaderContext';
import type { ScrollProviderContextType } from '../../../unlisted/ScrollProvider';

export default function useFullScreen(
  elementRef: RefObject<HTMLDivElement | null>,
  title1MaskRef: RefObject<HTMLHeadingElement | null>,
  title2MaskRef: RefObject<HTMLHeadingElement | null>,
  playButtonRef: RefObject<HTMLButtonElement | null>,
  shapePathRef: RefObject<SVGPathElement | null>,
  shapeContainerRef: RefObject<HTMLDivElement | null>,

  title1Ref: RefObject<HTMLHeadingElement | null>,
  title2Ref: RefObject<HTMLHeadingElement | null>,
  maskedElementsRef: RefObject<HTMLDivElement | null>,
  clipId: string,
): {
  enterFullScreen: () => void;
  exitFullScreen: () => void;
  isFullScreen: boolean;
} {
  const { siteHeaderElementRef } = useContext(SiteHeaderContext) as SiteHeaderContextType;
  const makeFullScreen = useRef<boolean>(false);
  const { goFullScreen, isFullScreen } = useBackgroundFan(
    shapePathRef,
    shapeContainerRef,
    maskedElementsRef,
    clipId,
  );
  const enterFullScreenTimeline = useSetupTimeline(enterFullScreenTransition, {
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
    title1Ref,
    title2Ref,
    siteHeaderElementRef,
  });
  const exitFullScreenTimeline = useSetupTimeline(exitFullScreenTransition, {
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
    title1Ref,
    title2Ref,
    siteHeaderElementRef,
  });

  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;

  const enterFullScreen = useCallback(() => {
    let currentY = scrollContext.scrollStatusRef.current?.offset.y;
    if (typeof currentY !== 'number') {
      currentY = 0;
    }
    const y = currentY + (elementRef.current?.getBoundingClientRect().top as number);
    const update = () => {
      makeFullScreen.current = true;
      exitFullScreenTimeline.kill();
      enterFullScreenTimeline.restart();
      goFullScreen(true);
    };

    if (y === currentY) {
      update();
    } else {
      scrollContext.scrollTo(0, y, 400, {
        easing: Quad.easeInOut,
        callback: () => {
          update();
        },
      });
    }
  }, [
    makeFullScreen,
    exitFullScreenTimeline,
    enterFullScreenTimeline,
    goFullScreen,
    scrollContext,
    elementRef,
  ]);

  const exitFullScreen = useCallback(() => {
    makeFullScreen.current = false;
    enterFullScreenTimeline.kill();
    exitFullScreenTimeline.restart();
    goFullScreen(false);
  }, [makeFullScreen, enterFullScreenTimeline, exitFullScreenTimeline, goFullScreen]);

  useScrollTrigger({
    trigger: elementRef,
    vars: {
      start: 'top bottom',
      end: 'bottom top',
      onUpdate() {
        if (makeFullScreen.current) {
          exitFullScreen();
        }
      },
    },
  });

  return {
    exitFullScreen,
    enterFullScreen,
    isFullScreen,
  };
}
